import { useMemo } from "react";
import { useLocation } from "wouter";
import { Settings } from "lucide-react";
import { Combobox } from "@/components/ui/combobox.jsx";
import { Button } from "@/components/ui/button.jsx";
import { useSessionTenant } from "@/contexts/SessionTenantContext.jsx";
import { LoadingSkeleton } from "@/components/ui/loading.jsx";

const ALARM_LEVELS = [
  ["danger", "Danger"],
  ["alerted", "Alerted"],
  ["normal", "Normal"],
  ["nodata", "No Data"],
];

function divider(value, label) {
  return { type: "divider", value, label };
}

function categoryOption(category, count) {
  return {
    value: `category:${category}`,
    label: category,
    count,
  };
}

/** Tag list filter: alarm state, groups, categories, Enalysis Metrics, plus manage link. */
export default function GroupsDropdown({
  groups = [],
  categories = [],
  groupAssetTagCounts = {},
  categoryTagCounts = {},
  alarmLevelCounts = {},
  enalysisMetricsCategory = null,
  enalysisSectionTotalCount = 0,
  enalysisUtilizationCategory = null,
  enalysisUtilizationCount = 0,
  enalysisRodLoadsCategory = null,
  enalysisRodLoadsCount = 0,
  enalysisBlowbyCategory = null,
  enalysisBlowbyCount = 0,
  isLoadingGroups = false,
  selectedFilterValue = "",
  setSelectedFilterValue,
}) {
  const [, setLocation] = useLocation();
  const { canManageTagGroups } = useSessionTenant();

  const options = useMemo(() => {
    const alarmOpts = ALARM_LEVELS.map(([key, label]) => ({
      value: `status:${key}`,
      label,
      count: alarmLevelCounts[key] ?? 0,
    }));

    const groupOpts = groups.map((g) => ({
      value: `group:${Number(g.TagGroupId)}`,
      label: g.TagGroupName,
      count: groupAssetTagCounts[Number(g.TagGroupId)] ?? 0,
    }));

    const catOpts = categories.map((c) => ({
      value: `category:${c}`,
      label: c,
      count: categoryTagCounts[c] ?? 0,
    }));

    const out = [
      divider("divider-alarm-levels", "Tag Alarm State"),
      ...alarmOpts,
    ];

    if (groupOpts.length) {
      out.push(divider("divider-groups", "Tag Groups"), ...groupOpts);
    }
    if (catOpts.length) {
      out.push(divider("divider-categories", "Categories"), ...catOpts);
    }

    if (enalysisMetricsCategory && enalysisSectionTotalCount > 0) {
      const subRows = [
        [enalysisUtilizationCategory, enalysisUtilizationCount],
        [enalysisBlowbyCategory, enalysisBlowbyCount],
        [enalysisRodLoadsCategory, enalysisRodLoadsCount],
      ]
        .filter(([c, n]) => c && n > 0)
        .map(([c, n]) => categoryOption(c, n));

      out.push(
        divider("divider-enalysis-metrics", "Enalysis Metrics"),
        {
          value: "enalysis:all",
          label: "All",
          count: enalysisSectionTotalCount,
        },
        ...subRows
      );
    }

    return out;
  }, [
    groups,
    categories,
    groupAssetTagCounts,
    categoryTagCounts,
    alarmLevelCounts,
    enalysisMetricsCategory,
    enalysisSectionTotalCount,
    enalysisUtilizationCategory,
    enalysisUtilizationCount,
    enalysisRodLoadsCategory,
    enalysisRodLoadsCount,
    enalysisBlowbyCategory,
    enalysisBlowbyCount,
  ]);

  if (isLoadingGroups) {
    return <LoadingSkeleton className='h-10 w-full rounded-md' />;
  }

  return (
    <Combobox
      value={selectedFilterValue}
      onValueChange={setSelectedFilterValue}
      options={options}
      placeholder='Select filter...'
      searchPlaceholder='Search filters...'
      emptyText='No filters found'
      multiple={false}
      closeOnSelect={false}
      className='rounded-md'
      footerContent={
        canManageTagGroups ? (
          <Button
            type='button'
            variant='ghost'
            size='sm'
            className='h-auto min-h-9 w-full min-w-0 items-start justify-start gap-2 !whitespace-normal px-2 py-2 text-left text-primary hover:bg-primary/10 hover:text-primary'
            onClick={() => setLocation("/manage-groups")}>
            <Settings
              className='mt-0.5 h-4 w-4 shrink-0 text-primary'
              aria-hidden
            />
            <span className='min-w-0 break-words leading-snug'>
              Manage Tag Groups
            </span>
          </Button>
        ) : null
      }
    />
  );
}
